import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Tx, QuickBuyTx } from "types";

interface TxStatusMap {
  [coinAddress: string]: {
    status: string | null;
    showModal: boolean;
    error_text?: string | null;
    // Секундомер сделки (shared/lib/txTiming): старт — приход pending с бэка, итог —
    // первый терминальный статус. Считает общий с веб-тостом модуль, здесь только
    // переносим готовые числа тем, кто рисует по монете (чип расширения).
    startTime?: number | null;
    finalMs?: number | null;
  };
}

interface txState {
  txs: Tx[];
  quickBuyTx: QuickBuyTx | null;
  txStatusMap: TxStatusMap;
  submittingCoinAddresses: string[];
}

const initialState: txState = {
  txs: [],
  quickBuyTx: null,
  txStatusMap: {},
  submittingCoinAddresses: [],
};

export const submitQuickBuyTx = createAction<QuickBuyTx>("tx/submitQuickBuyTx");

export const subscribeTxStatus = createAction<void>("tx/subscribeTxStatus");
export const unsubscribeTxStatus = createAction<void>("tx/unsubscribeTxStatus");

const txSlice = createSlice({
  name: "tx",
  initialState,
  reducers: {
    setTx(state, action: PayloadAction<Tx[]>) {
      state.txs = action.payload;
    },

    setQuickBuyTx(state, action: PayloadAction<QuickBuyTx>) {
      state.quickBuyTx = action.payload;
    },

    setTxStatus(
      state,
      action: PayloadAction<{
        coinAddress: string;
        status: string | null;
        error_text: string | null;
        startTime?: number | null;
        finalMs?: number | null;
      }>
    ) {
      const { coinAddress, status, error_text, startTime, finalMs } = action.payload;
      if (!state.txStatusMap[coinAddress]) {
        state.txStatusMap[coinAddress] = {
          status: null,
          showModal: false,
          error_text: null,
        };
      }
      state.txStatusMap[coinAddress].status = status;
      state.txStatusMap[coinAddress].error_text = error_text;
      state.txStatusMap[coinAddress].showModal = status !== null;
      state.txStatusMap[coinAddress].startTime = startTime ?? null;
      state.txStatusMap[coinAddress].finalMs = finalMs ?? null;
    },

    setModalForCoin(
      state,
      action: PayloadAction<{ coinAddress: string; showModal: boolean }>
    ) {
      const { coinAddress, showModal } = action.payload;
      if (!state.txStatusMap[coinAddress]) {
        state.txStatusMap[coinAddress] = { status: null, showModal };
      } else {
        state.txStatusMap[coinAddress].showModal = showModal;
      }
    },

    setSubmittingCoinAddresses(state, action: PayloadAction<string[]>) {
      state.submittingCoinAddresses = action.payload;
    },

    addSubmittingAddress(state, action: PayloadAction<string>) {
      if (!state.submittingCoinAddresses.includes(action.payload)) {
        state.submittingCoinAddresses.push(action.payload);
      }
    },

    removeSubmittingAddress(state, action: PayloadAction<string>) {
      state.submittingCoinAddresses = state.submittingCoinAddresses.filter(
        (addr) => addr !== action.payload
      );
    },

    resetTxStateForCoin: (
      state,
      action: PayloadAction<{ coinAddress: string }>
    ) => {
      const { coinAddress } = action.payload;
      delete state.txStatusMap[coinAddress];
      state.submittingCoinAddresses = state.submittingCoinAddresses.filter(
        (addr) => addr !== coinAddress
      );
    },

    clearAllTxData(state) {
      state.txStatusMap = {};
      state.submittingCoinAddresses = [];
    },

    resetSocket() {
      return initialState;
    },
  },
});

export const {
  setTx,
  resetSocket,
  setQuickBuyTx,
  setTxStatus,
  setModalForCoin,
  setSubmittingCoinAddresses,
  addSubmittingAddress,
  resetTxStateForCoin,
  removeSubmittingAddress,
  clearAllTxData,
} = txSlice.actions;

export default txSlice.reducer;
